'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import * as CustomPropTypes from '../../../constants/PropTypes';
import { LORAX, KOALA, GREAT_WHITE } from 'HubStyleTokens/colors';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import Big from 'UIComponents/elements/Big';
import UITable from 'UIComponents/table/UITable';
import UISection from 'UIComponents/section/UISection';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIBreakString from 'UIComponents/text/UIBreakString';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UILink from 'UIComponents/link/UILink';
import UIBadge from 'UIComponents/badge/UIBadge';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import * as Sync from 'ecosystem-marketplace-core/constants/dataSyncs/Sync';
import { getDataSyncsDataI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import DataSyncCard from 'ecosystem-marketplace-core/components/dataSync/DataSyncCard';
import { getDataSyncProductUrl } from '../../../utils/urls';
const I18N_PATH = 'appMarketplace.detail.overview.sharedData';
const SYNC_DIRECTION_ICON_SIZE = 24;

const EmptyComponent = () => {
  return null;
};

const DataSyncBadge = styled(UIBadge).withConfig({
  displayName: "SharedData__DataSyncBadge",
  componentId: "d7ezpf-0"
})(["background-color:", ";height:18px;margin-bottom:20px;&:hover{background-color:", ";}"], KOALA, GREAT_WHITE);

const SyncDirectionIcon = ({
  sync
}) => {
  let iconName;

  switch (sync) {
    case Sync.INBOUND:
      iconName = 'dataSyncRight';
      break;

    case Sync.OUTBOUND:
      iconName = 'dataSyncLeft';
      break;

    case Sync.BOTH:
      iconName = 'dataSync';
      break;

    default:
  }

  if (iconName) {
    return /*#__PURE__*/_jsx(UIIcon, {
      name: iconName,
      color: LORAX,
      size: SYNC_DIRECTION_ICON_SIZE
    });
  } else {
    return null;
  }
};

const SharedData = ({
  dataSyncHowItWorks,
  dataSyncInfo,
  dataSyncs,
  iconUrl,
  name,
  onTrackInteraction,
  opsHub
}) => {
  const renderedSharedDataRows = useMemo(() => dataSyncs.map((dataSync, index) => {
    return /*#__PURE__*/_jsxs("tr", {
      children: [/*#__PURE__*/_jsx("td", {
        children: /*#__PURE__*/_jsx(Big, {
          style: {
            fontWeight: 500
          },
          children: dataSync.get('target')
        })
      }), /*#__PURE__*/_jsx(UITooltip, {
        onOpenChange: ({
          target: {
            value: open
          }
        }) => {
          if (open) {
            onTrackInteraction('hover shared data description', 'overview');
          }
        },
        placement: "top",
        title: dataSync.get('description') || /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_PATH}.dataFlow${dataSync.get('sync')}`,
          options: {
            name
          }
        }),
        children: /*#__PURE__*/_jsx("td", {
          className: "text-center",
          children: /*#__PURE__*/_jsx(SyncDirectionIcon, {
            sync: dataSync.get('sync')
          })
        })
      }, `shared-data-${index}`), /*#__PURE__*/_jsx("td", {
        children: /*#__PURE__*/_jsx(Big, {
          style: {
            fontWeight: 500
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getDataSyncsDataI18nKey(dataSync.get('data'))
          })
        })
      })]
    }, `shared-data-${index}`);
  }), [dataSyncs, onTrackInteraction, name]);
  const renderedDataSyncInfo = dataSyncInfo && !opsHub ? /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("b", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_PATH}.moreInfoText`
      })
    }), /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(UIBreakString, {
        children: dataSyncInfo
      })
    })]
  }) : null;
  const renderedHowItWorks = dataSyncHowItWorks && opsHub ? /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("p", {
      className: "m-bottom-1",
      children: /*#__PURE__*/_jsx("b", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${I18N_PATH}.howItWorks`
        })
      })
    }), /*#__PURE__*/_jsx(UIBreakString, {
      children: /*#__PURE__*/_jsx(SanitizedText, {
        body: dataSyncHowItWorks
      })
    })]
  }) : null;
  const renderedDataSyncs = useMemo(() => {
    if (opsHub) {
      return /*#__PURE__*/_jsx("div", {
        style: {
          width: '80%'
        },
        children: dataSyncs.map((dataSync, index) => /*#__PURE__*/_jsx(DataSyncCard, {
          dataSync: dataSync,
          iconUrl: iconUrl,
          onToggleOpen: (fieldType, isFieldMappingSectionOpen) => onTrackInteraction('toggle field mapping section open', 'overview', fieldType, {
            isFieldMappingSectionOpen
          }),
          SyncIcon: SyncDirectionIcon
        }, `data-sync-card-${index}`))
      });
    } else {
      return /*#__PURE__*/_jsxs(UITable, {
        hover: false,
        style: {
          width: '80%'
        },
        children: [/*#__PURE__*/_jsx("thead", {
          children: /*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx("th", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `${I18N_PATH}.appData`,
                options: {
                  name
                }
              })
            }), /*#__PURE__*/_jsx("th", {
              className: "text-center",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `${I18N_PATH}.dataFlow`
              })
            }), /*#__PURE__*/_jsx("th", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: `${I18N_PATH}.hubSpotData`
              })
            })]
          })
        }), /*#__PURE__*/_jsx("tbody", {
          children: renderedSharedDataRows
        })]
      });
    }
  }, [opsHub, dataSyncs, iconUrl, name, renderedSharedDataRows, onTrackInteraction]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [opsHub && /*#__PURE__*/_jsx(UILink, {
      href: getDataSyncProductUrl(getLang()),
      onClick: () => onTrackInteraction('click data sync badge', 'overview'),
      external: true,
      ExternalIcon: EmptyComponent,
      children: /*#__PURE__*/_jsx(DataSyncBadge, {
        use: "koala",
        children: /*#__PURE__*/_jsx(UIMediaObject, {
          itemLeft: /*#__PURE__*/_jsx(UIIcon, {
            name: "dataSync",
            size: "xxs",
            color: LORAX,
            style: {
              marginTop: 1
            }
          }),
          spacing: "none",
          style: {
            marginTop: 1
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `${I18N_PATH}.badge`
          })
        })
      })
    }), renderedHowItWorks, /*#__PURE__*/_jsx(UISection, {
      children: renderedDataSyncs
    }), renderedDataSyncInfo]
  });
};

SharedData.propTypes = {
  dataSyncHowItWorks: PropTypes.string,
  dataSyncInfo: PropTypes.string,
  dataSyncs: ImmutablePropTypes.listOf(CustomPropTypes.dataSync).isRequired,
  iconUrl: PropTypes.string,
  name: PropTypes.string.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  opsHub: PropTypes.bool
};
export default SharedData;